import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@civilgrid/ui/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { getPgeCloudSyncFolders, type PgeCloudSyncFolder, triggerPgeCloudSyncWorkflow } from '~/api/internal';
import type { OrganizationDetail } from '~/common/types';
import Button from '~/components2/forms/Button';
import { Notify } from '~/components2/Notify';
import OrganizationPicker from '~/components2/OrganizationPicker';

export function FolderList({
  folders,
  selectedPrefix,
  onSelect,
}: {
  folders: PgeCloudSyncFolder[];
  selectedPrefix: string | null;
  onSelect: (prefix: string) => void;
}) {
  if (folders.length === 0) {
    return (
      <div className="text-gray-500" data-testid="pge-cloud-sync-no-folders">
        No sync folders found. Upload files to the configured S3 prefix first, then refresh.
      </div>
    );
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <RadioGroup
      value={selectedPrefix ?? ''}
      onValueChange={onSelect}
      aria-label="Sync folders"
      className="gap-1"
      data-testid="pge-cloud-sync-folder-list"
    >
      {folders.map((folder) => (
        <div
          key={folder.prefix}
          className="flex items-center gap-2"
          data-testid={`pge-cloud-sync-folder-option-${folder.name}`}
        >
          <RadioGroupItem
            value={folder.prefix}
            id={`pge-cloud-sync-folder-${folder.name}`}
            disabled={!folder.hasRequiredFiles}
            data-testid={`pge-cloud-sync-folder-radio-${folder.name}`}
          />
          <Label
            htmlFor={`pge-cloud-sync-folder-${folder.name}`}
            className={folder.hasRequiredFiles ? '' : 'text-gray-400'}
          >
            {folder.name} ({folder.fileCount} file{folder.fileCount === 1 ? '' : 's'}
            {folder.lastModified ? ` — updated ${formatDate(folder.lastModified)}` : ''})
            {folder.hasRequiredFiles ? '' : ' — needs exactly one .xlsx and one .geojson/.json'}
          </Label>
        </div>
      ))}
    </RadioGroup>
  );
}

export default function PgeCloudSync() {
  const [selectedPrefix, setSelectedPrefix] = useState<string | null>(null);
  const [targetOrganization, setTargetOrganization] = useState<OrganizationDetail | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const foldersQuery = useQuery({
    queryKey: ['pgeCloudSync', 'folders'],
    queryFn: getPgeCloudSyncFolders,
    refetchOnWindowFocus: false,
  });

  const triggerMutation = useMutation({
    mutationFn: ({ sourcePrefix, organizationId }: { sourcePrefix: string; organizationId: string }) =>
      triggerPgeCloudSyncWorkflow(sourcePrefix, organizationId),
    onSuccess: () => {
      Notify.info({ message: 'Cloud sync workflow triggered' });
    },
    onError: (err) => {
      let message = 'Failed to start cloud sync workflow';
      if (err instanceof AxiosError) message = err.response?.data?.reason || message;
      Notify.error({ message, duration: 10_000 });
    },
  });

  const folders = foldersQuery.data?.folders ?? [];

  return (
    <div className="flex flex-col items-start space-y-6 text-sm" data-testid="pge-cloud-sync-page">
      <div>
        <div className="mb-2 font-medium">Instructions</div>
        <p className="mb-3 text-gray-600 text-sm">
          Complete the following steps in order. When finished, trigger the workflow below.
        </p>
        <ol className="list-inside list-decimal space-y-2">
          <li>
            Obtain the Book of Work Excel workbook from Sam.
            <ul className="mt-1 ml-4 list-inside list-disc space-y-1">
              <li>Verify formatting against previous Book of Work deliverables.</li>
            </ul>
          </li>
          <li>Obtain the WIP Cloud GeoJSON file from Braden.</li>
          <li>
            In S3, under <code>data-imports/pge-cloud-sync/</code>, create a new folder named{' '}
            <strong>sync-&lt;date&gt;</strong> (use the current date).
          </li>
          <li>Upload both the Book of Work Excel file and the WIP Cloud GeoJSON file to the new folder.</li>
          <li>
            Refresh the folder list, select the new folder, pick the target organization, and trigger the workflow.
          </li>
        </ol>
      </div>

      <section className="w-full max-w-2xl space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-medium">Sync folders</div>
          <Button
            alternate
            onClick={() => foldersQuery.refetch()}
            isProcessing={foldersQuery.isFetching}
            data-testid="pge-cloud-sync-refresh-folders"
          >
            Refresh
          </Button>
        </div>

        {foldersQuery.isLoading && <div className="text-gray-500">Loading folders…</div>}
        {foldersQuery.isError && (
          <div className="text-red-500" data-testid="pge-cloud-sync-folders-error">
            Failed to load sync folders. Refresh to try again.
          </div>
        )}
        {foldersQuery.isSuccess && (
          <FolderList folders={folders} selectedPrefix={selectedPrefix} onSelect={setSelectedPrefix} />
        )}
      </section>

      <section className="w-full max-w-2xl space-y-2">
        <div className="font-medium">Target organization</div>
        <p className="text-gray-600">
          The synced projects are created/updated in this organization. Only orgs opted in for cloud sync (
          <code>metadata.pgeCloudSyncTarget</code>) can be targeted — any other org is rejected at trigger time.
        </p>
        <div data-testid="pge-cloud-sync-org-picker">
          <OrganizationPicker
            selectedOrganization={targetOrganization}
            onOrganizationSelect={(_id, organization) => setTargetOrganization(organization)}
            placeholder="Select target organization"
          />
        </div>
      </section>

      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogTrigger asChild>
          <Button
            disabled={!selectedPrefix || !targetOrganization || triggerMutation.isPending}
            isProcessing={triggerMutation.isPending}
            data-testid="pge-cloud-sync-trigger"
          >
            Trigger Workflow
          </Button>
        </DialogTrigger>
        <DialogContent
          title="Trigger Workflow Confirmation"
          className="z-[110] w-auto min-w-min max-w-[75vw]"
          overlayClassName="z-[100]"
          data-testid="pge-cloud-sync-trigger-dialog"
        >
          <DialogBody className="text-sm">
            <p>
              This will sync <code>{selectedPrefix}</code> against the projects of{' '}
              <strong>{targetOrganization?.name}</strong> and{' '}
              <strong>apply boundary, project manager, and address updates immediately</strong>. A snapshot CSV, change
              log, and geometry comparisons will be written back to the same S3 folder. Please confirm that both input
              files and the target organization are correct before continuing.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button alternate onClick={() => setIsConfirmOpen(false)} data-testid="pge-cloud-sync-trigger-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedPrefix && targetOrganization) {
                  triggerMutation.mutate({ sourcePrefix: selectedPrefix, organizationId: targetOrganization.id });
                }
                setIsConfirmOpen(false);
              }}
              isProcessing={triggerMutation.isPending}
              disabled={triggerMutation.isPending}
              data-testid="pge-cloud-sync-trigger-confirm"
            >
              Trigger Workflow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
